import { createRouter, createWebHistory } from "vue-router";
import GroupView from "../views/Group.vue";
import Agenda from "../views/Agenda.vue";
import Quotes from "../views/Quotes.vue";
import AuthenticationLayout from "../layouts/Authentication.vue";
import NavigationLayout from "../layouts/Navigation.vue";
import SinglePageLayout from "../layouts/SinglePage.vue";
import InviteMembersFollowupView from "../views/InviteMembersFollowup.vue";
import NotificationSettingsView from "../views/settings/NotificationSettings.vue";
import ViewEventView from "../views/ViewEvent.vue";
import SettingsView from "../views/Settings.vue";
import Dice from "../views/games/Dice.vue";
import Wordle from "@/views/games/wordle/Wordle.vue";
import Wordle6 from "@/views/games/wordle6/Wordle6.vue";
import Dilemmas from "@/views/games/dilemmas/Dilemmas.vue";
import GroupUserProfileSettingsView from "../views/settings/GroupUserProfileSettings.vue";
import GroupProfileSettingsView from "../views/settings/GroupProfileSettings.vue";
import LoginWithProviderView from "../views/LoginWithProvider.vue";
import MemberPermissionsView from "../views/settings/MemberPermissions.vue";
import MemberSettingsView from "../views/settings/MemberSettings.vue";
import CompleteProfile from "../views/CompleteProfile.vue";
import CreateSelectGroup from "@/views/CreateSelectGroup.vue";
import QuoteComments from "../views/QuoteComments.vue";
import LeaveGroupView from "../views/settings/LeaveGroup.vue";
import DeleteGroupView from "../views/settings/DeleteGroup.vue";
import LoginRegister from "../views/LoginRegister.vue";
import VerifyEmail from "../views/VerifyEmail.vue";
import Photos from "../views/Photos.vue";
import Shorts from "../views/Shorts.vue";
import Games from "../views/games/GameList.vue";
import EmailAction from "../views/EmailAction.vue";
import PhotoComments from "../views/PhotoComments.vue";
import DilemmaComments from "../views/DilemmaComments.vue";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { logEvent } from "firebase/analytics";
import DeleteAccount from "../views/DeleteAccount.vue";
import DeleteAccountInfo from "@/views/settings/DeleteAccountInfo.vue";
import PrivacyPolicyView from "@/views/PrivacyPolicyView.vue";
import EulaView from "@/views/EulaView.vue";
import { setLocalStorageItem } from "../helpers/utils/local-storage";
import { showBannerAdIfNeeded } from "@/helpers/ads/ad-banner-manager";
import { hideBannerAd } from "@/helpers/flutter/features/ads";
import { useAppStore } from "@/stores/modules/app/app";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";
import { useQuoteFilterStore } from "@/stores/modules/quotes/quotes-filter";

const routes = [
  {
    // path: "*",
    path: "/:catchAll(.*)",
    name: "NotFound",
    meta: {
      catchAll: true,
    },
  },
  {
    path: "/invite-link",
    name: "InviteLinkFollowup",
    component: InviteMembersFollowupView,
    meta: {
      requiresAuth: true,
      requiresEmailVerified: true,
      requiresCompleteProfile: true,
    },
  },
  {
    path: "/auth",
    name: "Auth",
    component: AuthenticationLayout,
    meta: {},
    children: [
      {
        path: "/delete-account",
        name: "DeleteAccount",
        component: DeleteAccount,
        meta: {
          requiresAuth: true,
          requiresEmailVerified: true,
          requiresCompleteProfile: true,
          requiresEmptyGroupList: true,
        },
      },
      {
        path: "/start",
        name: "GroupSelector",
        component: CreateSelectGroup,
        meta: {
          requiresAuth: true,
          requiresEmailVerified: true,
          requiresCompleteProfile: true,
        },
      },
      {
        path: "/login",
        name: "Login",
        component: LoginWithProviderView,
        meta: {},
      },
      {
        path: "/login-email",
        name: "LoginEmail",
        component: LoginRegister,
        meta: {},
      },
      {
        path: "/complete-profile",
        name: "CompleteProfile",
        component: CompleteProfile,
        meta: {
          requiresAuth: true,
          requiresEmailVerified: true,
        },
      },
      {
        path: "/verify-email",
        name: "VerifyEmail",
        component: VerifyEmail,
        meta: { requiresAuth: true },
      },
      {
        path: "/email-action",
        name: "EmailAction",
        component: EmailAction,
      },
    ],
  },
  //
  {
    name: "PrivacyPolicy",
    path: "/privacy-policy",
    component: PrivacyPolicyView,
  },
  {
    name: "Eula",
    path: "/eula-policy",
    component: EulaView,
  },

  //Single pages inside the app
  {
    name: "SinglePages",
    path: "/single-page",
    component: SinglePageLayout,
    meta: {
      requiresAuth: true,
      requiresCompleteProfile: true,
      requiresGroup: true,
      requiresEmailVerified: true,
    },
    children: [
      {
        path: "/games/wordle",
        name: "wordle",
        component: Wordle,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/games/wordle6",
        name: "wordle6",
        component: Wordle6,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/games/dice",
        name: "dice",
        component: Dice,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/dilemmas/:dilemmaId?",
        name: "Dilemmas",
        component: Dilemmas,
        props: true,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/agenda/view/:groupId/:eventId",
        name: "viewEvent",
        component: ViewEventView,
        props: (route) => ({
          ...route.params,
        }),
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings",
        name: "settings",
        component: SettingsView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/group-profile",
        name: "groupUserProfileSettings",
        component: GroupUserProfileSettingsView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/group",
        name: "groupProfileSettings",
        component: GroupProfileSettingsView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/permissions",
        name: "memberPermissions",
        component: MemberPermissionsView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/leave",
        name: "leaveGroup",
        component: LeaveGroupView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/delete",
        name: "deleteGroup",
        component: DeleteGroupView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/members",
        name: "memberSettings",
        component: MemberSettingsView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/notifications",
        name: "notificationSettingsView",
        component: NotificationSettingsView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/settings/delete-account-information",
        name: "deleteAccountInfo",
        component: DeleteAccountInfo,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/quotes/comments/:groupId/:quoteId",
        name: "commentQuote",
        component: QuoteComments,
        props: (route) => ({
          ...route.params,
        }),
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/piccas/comments/:groupId/:photoId",
        name: "commentPhoto",
        component: PhotoComments,
        props: (route) => ({
          ...route.params,
        }),
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
      {
        path: "/dilemmas/comments/:groupId/:dilemmaId",
        name: "DilemmaComments",
        component: DilemmaComments,
        props: (route) => ({
          ...route.params,
        }),
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          requiresEmailVerified: true,
          showBannerAd: true,
        },
      },
    ],
  },
  //Pages that have the navigation bar
  {
    path: "/",
    name: "NavigationPages",
    component: NavigationLayout,
    meta: {
      requiresAuth: true,
      requiresCompleteProfile: true,
      requiresGroup: true,
      requiresEmailVerified: true,
    },
    children: [
      {
        path: "/",
        name: "Group",
        component: GroupView,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          showBannerAd: true,
        },
      },
      {
        path: "/agenda",
        name: "Agenda",
        component: Agenda,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          showBannerAd: true,
        },
      },
      {
        path: "/quotes",
        name: "Quotes",
        component: Quotes,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          showBannerAd: true,
        },
      },
      {
        path: "/piccas",
        name: "Photos",
        component: Photos,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          showBannerAd: true,
        },
      },
      {
        path: "/shorts",
        name: "Shorts",
        component: Shorts,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          showBannerAd: true,
        },
      },
      {
        path: "/games",
        name: "Games",
        component: Games,
        meta: {
          requiresAuth: true,
          requiresCompleteProfile: true,
          requiresGroup: true,
          showBannerAd: true,
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Wait for app initialization before proceeding with any route
router.beforeEach(async () => {
  const appStore = useAppStore();
  await appStore.waitForFinalization();
});

router.beforeEach(async (to) => {
  const forceGroup = to.query.forceGroup;

  if (forceGroup) {
    const userStore = useUserStore();

    // Switch group (handles toasts, optimistic update, and server sync internally)
    try {
      await userStore.switchActiveGroup(forceGroup);
    } catch (error) {
      // Error already handled in the action (toast shown, rollback done)
      // Just log it here
      console.error("Group switch failed:", error);
    }

    // Remove forceGroup parameter from URL and continue navigation
    const { forceGroup: _, ...remainingQuery } = to.query;
    return {
      ...to,
      query: remainingQuery,
      replace: true,
    };
  }
});

router.beforeEach((to) => {
  if (to.name === "InviteLinkFollowup") {
    //save the token
    const token = to.query?.token;
    if (token) {
      setLocalStorageItem("joinToken", String(token));
    }
  }
});

router.beforeEach((to) => {
  if (to.matched.some((x) => x.meta.requiresEmptyGroupList)) {
    const userStore = useUserStore();
    if (userStore.userData?.groups?.length > 0) {
      return { name: "Group" };
    }
  }
});

router.beforeEach((to) => {
  const requiresAuth = to.matched.some((x) => x.meta.requiresAuth);
  if (requiresAuth) {
    const authStore = useAuthStore();
    if (!authStore.firebaseAuthUser) {
      return { name: "Login" };
    }
  }
});

router.beforeEach((to) => {
  const requiresEmailVerified = to.matched.some(
    (x) => x.meta.requiresEmailVerified,
  );
  //TODO investigate with some apple provider sign ins do not have a verified email.
  const authStore = useAuthStore();
  const user = authStore.firebaseAuthUser;
  const providers = user?.providerData;
  const hasSignedInWithApple = providers?.some(
    (provider) => provider.providerId === "apple.com",
  );

  if (!requiresEmailVerified || hasSignedInWithApple || user?.emailVerified) {
    return;
  }
  return { name: "VerifyEmail" };
});

router.beforeEach((to) => {
  const requiresCompleteProfile = to.matched.some(
    (x) => x.meta.requiresCompleteProfile,
  );
  if (requiresCompleteProfile) {
    const userStore = useUserStore();
    if (!userStore.hasUserDoc) {
      return { name: "CompleteProfile" };
    }
  }
});

router.beforeEach((to) => {
  const requiresOrganiserRole = to.matched.some(
    (x) => x.meta.requiresOrganiserRole,
  );
  if (requiresOrganiserRole) {
    const groupsStore = useGroupsStore();
    if (!groupsStore.isOrganiser && !groupsStore.isAdmin) {
      return { name: "Quotes" };
    }
  }
});

router.beforeEach((to) => {
  if (to.matched.some((x) => x.meta.catchAll)) {
    return { name: "Group" };
  }
});

router.beforeEach((to) => {
  const requiresGroup = to.matched.some((x) => x.meta.requiresGroup);
  if (requiresGroup) {
    const userStore = useUserStore();
    if (!userStore.activeGroup) {
      return { name: "GroupSelector" };
    }
  }
});

router.beforeEach((to) => {
  if (to.name === "GroupSelector") {
    const userStore = useUserStore();
    if (userStore.activeGroup) {
      return { name: "Group" };
    }
  }
});

router.beforeEach((to) => {
  if (to.name === "CompleteProfile") {
    const userStore = useUserStore();
    if (userStore.hasUserDoc) {
      return { name: "Group" };
    }
  }
});

router.beforeEach((to) => {
  //is already signed in for some reason.
  if (to.name === "LoginLink") {
    const authStore = useAuthStore();
    if (authStore.firebaseAuthUser) {
      return { name: "Group" };
    }
  }
});

router.beforeEach((to) => {
  //is already signed in for some reason.
  if (to.name === "Login") {
    const authStore = useAuthStore();
    if (authStore.firebaseAuthUser) {
      return { name: "Group" };
    }
  }
});

router.afterEach((to, from) => {
  logEvent(firebaseAnalytics, "route_changed", {
    to: to?.name,
    from: from?.name,
  });
});

router.afterEach((to) => {
  const shouldShowBannerAd = to.matched.some((x) => x.meta.showBannerAd);

  if (shouldShowBannerAd) {
    const groupsStore = useGroupsStore();
    const currentGroup = groupsStore.groupData;
    showBannerAdIfNeeded(currentGroup);
  } else {
    hideBannerAd();
  }
});

export default router;
